import { StateNode } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import type { TLShape } from "@tldraw/tlschema";
import { newTracingSheet } from "./tracing-sheet";

/** Placement is local until the click: cancel never creates a shared record. */
export class TracePlacementTool extends StateNode {
  static override id = "trace-paper";
  width = 600;
  height = 424;
  override onEnter(info: { over?: TLShape } = {}) {
    const bounds = info.over && this.editor.getShapePageBounds(info.over);
    const viewport = this.editor.getViewportPageBounds();
    this.width = bounds?.w ?? Math.min(900, viewport.w * 0.65);
    this.height = bounds?.h ?? this.width * 0.707;
    this.editor.selectNone();
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerUp() {
    if (this.editor.getIsReadonly()) return this.onCancel();
    const point = this.editor.inputs.getCurrentPagePoint();
    newTracingSheet(this.editor, undefined, {
      x: point.x - this.width / 2,
      y: point.y - this.height / 2,
      w: this.width,
      h: this.height,
    });
  }
  override onCancel() {
    this.editor.setCurrentTool("select");
  }
}

export function TracePlacementPreview({ editor }: { editor: Editor }) {
  const preview = useValue(
    "tracing paper placement",
    () => {
      if (!editor.isIn("trace-paper")) return null;
      const tool = editor.getCurrentTool() as TracePlacementTool;
      const point = editor.pageToScreen(editor.inputs.getCurrentPagePoint());
      const zoom = editor.getZoomLevel();
      return {
        x: point.x,
        y: point.y,
        w: tool.width * zoom,
        h: tool.height * zoom,
      };
    },
    [editor],
  );
  if (!preview) return null;
  return (
    <div
      aria-hidden="true"
      className="trace-placement-preview"
      style={{
        left: preview.x - preview.w / 2,
        top: preview.y - preview.h / 2,
        width: preview.w,
        height: preview.h,
      }}
    />
  );
}
